import { HelmetProvider } from "react-helmet-async";
import {
  withAuthInfo,
  AuthProvider,
  WithAuthInfoProps,
  createClient,
  IAuthClient,
} from "@propelauth/react";

import { AuthContext } from "../utils/context";
import { memo, useMemo } from "react";
import { useOrgCheck, ENABLE_ORG_CHECK } from "../hooks/useOrgCheck";
import { OrgPicker } from "./org-picker/OrgPicker";

const PropAuthWrapper = ({
  isLoggedIn,
  accessToken,
  user,
  isImpersonating,
  accessTokenExpiresAtSeconds,
  userClass,
  orgHelper,
  impersonatorUserId,
  accessHelper,
  getAccessToken,
  refreshAuthInfo,
  tokens,
  VITE_APP_AUTH_URL,
  enableOrgCheck = true,
  ...restProps
}: WithAuthInfoProps & {
  VITE_APP_AUTH_URL?: string;
  enableOrgCheck?: boolean;
  children?: React.ReactNode;
}) => {
  const client = new URL(window.location.href).searchParams.get("client");

  const getSamlUrl = (client: string | null): string => {
    return client
      ? `${VITE_APP_AUTH_URL}/api/fe/v3/login/saml?org_name=${client}&rt=${btoa(window.location.href)}`
      : `${VITE_APP_AUTH_URL}/api/fe/v3/login/saml?rt=${btoa(window.location.href)}`;
  };

  const { orgState, joinableOrgs, joinOrg } = useOrgCheck({
    isLoggedIn,
    orgHelper,
    skip: !enableOrgCheck || !ENABLE_ORG_CHECK,
  });

  if (!isLoggedIn) {
    window.location.href = getSamlUrl(client);
  }

  if (orgState === "checking") return null;
  if (orgState === "picking") {
    return <OrgPicker orgs={joinableOrgs} onSelect={joinOrg} />;
  }

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn: isLoggedIn,
        accessToken: accessToken,
        ssoUser: user ?? null,
        isImpersonating: isImpersonating,
        accessTokenExpiresAtSeconds: accessTokenExpiresAtSeconds,
        userClass: userClass,
        orgHelper: orgHelper,
        impersonatorUserId: impersonatorUserId ?? null,
        accessHelper: accessHelper,
      }}
    >
      {restProps?.children}
    </AuthContext.Provider>
  );
};

const WrappedRootWrapper = withAuthInfo(PropAuthWrapper);

export const RootWrapper = memo(
  ({
    VITE_APP_AUTH_URL,
    enableOrgCheck = true,
    children,
  }: {
    VITE_APP_AUTH_URL: string;
    enableOrgCheck?: boolean;
    children: React.ReactNode;
  }) => {
    // /map-overlay/* is a token-driven embed, never a real login, so disable PropelAuth's background
    // token refresh here. Left on, it eventually redirects this unauthenticated page to a login screen.
    const isMapOverlayCapture = window.location.pathname.startsWith("/map-overlay/");
    const mapOverlayAuthClient = useMemo<IAuthClient | undefined>(() => {
      if (!isMapOverlayCapture || !VITE_APP_AUTH_URL) return undefined;
      return createClient({
        authUrl: VITE_APP_AUTH_URL,
        enableBackgroundTokenRefresh: false,
        disableRefreshOnFocus: true,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps -- route/authUrl are stable for the app's lifetime
    }, []);

    if (!VITE_APP_AUTH_URL) {
      return <div>VITE_APP_AUTH_URL is not set.</div>;
    }

    return (
      <AuthProvider {...(mapOverlayAuthClient ? { client: mapOverlayAuthClient } : { authUrl: VITE_APP_AUTH_URL })}>
        <HelmetProvider>
          <WrappedRootWrapper VITE_APP_AUTH_URL={VITE_APP_AUTH_URL} enableOrgCheck={enableOrgCheck}>
            {children}
          </WrappedRootWrapper>
        </HelmetProvider>
      </AuthProvider>
    );
  },
);
